import fetchAuthenticatedUser from '@Application/Security/Authentications/fetchAuthenticatedUser';
import AuthenticatedUserContext, {
	type AuthenticatedUserContextValue,
} from '@UI/Providers/AuthenticatedUserProvider/AuthenticatedUserContext';
import * as React from 'react';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Spinner } from '@verclam/ui-kit';
import RootPage from '@Guidelines/Layout/RootPage';

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Holds every descendant behind a loading state until the session probe resolves: children mount
 * only once `sessionStatus` has left `'unknown'`, so no guard and no page ever renders against an
 * unresolved session.
 */
const AuthenticatedUserProvider: React.FC<Props> = ({ children }) => {
	const dispatch = useDispatch();

	const { user, sessionStatus } = useSelector(
		(state) => ({
			user: state.authenticatedUserStore.user,
			sessionStatus: state.authenticatedUserStore.sessionStatus,
		}),
		(previous, next) => previous.user === next.user && previous.sessionStatus === next.sessionStatus,
	);

	// No cache: this is a fetch-once probe. `stopOn` only re-opens when `sessionStatus` goes back to
	// `'unknown'`, which only happens on `USER_REVOKED` — so a logout re-probes the server instead of
	// trusting the client's local state, closing the gap left by resetting to `initialState`.
	const { isLoading } = useStoreQuery({
		selector: (state) => state.authenticatedUserStore.sessionStatus,
		stopOn: (status) => status !== 'unknown',
		query: () => dispatch(fetchAuthenticatedUser()),
	});

	const value = React.useMemo<AuthenticatedUserContextValue | null>(() => {
		if (isLoading) {
			return null;
		}

		return { user, sessionStatus };
	}, [user, sessionStatus, isLoading]);

	if (isLoading) {
		return (
			<RootPage>
				<Spinner aria-label="Chargement de l'application en cours...." />
			</RootPage>
		);
	}

	return <AuthenticatedUserContext.Provider value={value}>{children}</AuthenticatedUserContext.Provider>;
};

export default AuthenticatedUserProvider;
